import axios from 'axios';
import { getAuth } from 'firebase/auth';

// Configuración base para las peticiones al API según el entorno
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5004'
  : (process.env.REACT_APP_API_HEALTH_PATIENT_URL);

// Prefijo de API según entorno
const API_PREFIX = process.env.NODE_ENV === 'development' ? '/api' : '';

// URL completa
const API_URL = `${API_BASE}${API_PREFIX}`;

// Variables para el sistema de caché
const apiCache = {
  healthCenters: {},
  searchResults: {},
  lastFetch: {}
};

// Variables para el control de frecuencia de solicitudes
let requestTimestamps = [];
const MAX_REQUESTS_PER_MINUTE = 30;

// Objeto para almacenar promesas en curso
const pendingRequests = {};

// Función para verificar si podemos realizar una nueva solicitud
const canMakeRequest = () => {
  const now = Date.now();
  // Eliminar timestamps más antiguos que 1 minuto
  requestTimestamps = requestTimestamps.filter(time => now - time < 60000);
  
  // Si hay demasiadas solicitudes en cola, limpiarlas todas para evitar el bucle
  if (requestTimestamps.length >= MAX_REQUESTS_PER_MINUTE) {
    console.warn(`Demasiadas solicitudes (${requestTimestamps.length}/${MAX_REQUESTS_PER_MINUTE}). Reiniciando contador.`);
    requestTimestamps = [];
    return true; // Permitir esta solicitud para evitar bloqueos
  }
  
  // Verificar si estamos por debajo del límite
  return requestTimestamps.length < MAX_REQUESTS_PER_MINUTE;
};

// Función para registrar una nueva solicitud
const registerRequest = () => {
  requestTimestamps.push(Date.now());
};

// Función para reintento con retroceso exponencial
const retryWithExponentialBackoff = async (fn, retries = 3, initialDelay = 1000, maxDelay = 10000) => {
  let lastError;
  let delay = initialDelay;
 
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
 
      // Si no es error 429 o se acabaron los reintentos, fallar
      if ((error.response?.status !== 429) || attempt === retries) {
        throw error;
      }
 
      console.warn(`Intento ${attempt + 1}/${retries + 1} falló con 429, reintentando en ${delay}ms...`);
 
      // Capturar el valor actual de delay en una constante
      const currentDelay = delay;
 
      // Esperar con retroceso exponencial
      await new Promise(resolve => setTimeout(resolve, currentDelay));
 
      // Aumentar el tiempo de espera para el próximo intento (retroceso exponencial)
      delay = Math.min(delay * 2, maxDelay);
    }
  }
 
  throw lastError;
};

// Función para obtener o crear una solicitud
const getOrCreateRequest = (key, requestFn) => {
  if (!pendingRequests[key]) {
    // Crear una nueva promesa y guardarla
    pendingRequests[key] = requestFn()
      .finally(() => {
        // Limpiar la referencia cuando termine
        setTimeout(() => {
          delete pendingRequests[key];
        }, 100);
      });
  }
  
  // Devolver la promesa existente o recién creada
  return pendingRequests[key];
};

// Modificar la configuración de axios
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000 // 30 segundos
});

// Interceptor para añadir el token de autenticación de Firebase y controlar frecuencia
apiClient.interceptors.request.use(
  async (config) => {
    // Limitar intentos a 5 para evitar bucles infinitos
    let attempts = 0;
    const maxAttempts = 5;
    
    while (!canMakeRequest() && attempts < maxAttempts) {
      console.warn(`Esperando para no exceder límite de solicitudes... Intento ${attempts + 1}/${maxAttempts}`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }
    
    // Si después de varios intentos seguimos sin poder hacer la solicitud, continuar de todos modos
    if (!canMakeRequest()) {
      console.error('Forzando solicitud después de varios intentos fallidos');
      // Limpiar el historial de solicitudes para evitar futuros bloqueos
      requestTimestamps = [];
    }
    
    // Registrar esta solicitud
    registerRequest();
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const token = await user.getIdToken();
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error al obtener token de Firebase:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Agregar interceptor de respuesta para depuración y manejo de errores 429
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Silenciar 404 en endpoints donde "no encontrado" es un caso válido
    const url = error.config?.url || '';
    const is404 = error.response?.status === 404;
    const isOptionalEndpoint = url.includes('/appointments') ||
      url.includes('/patient-records') ||
      url.includes('/patient-profiles');

    // Solo loguear si NO es un 404 en endpoints opcionales
    if (!(is404 && isOptionalEndpoint)) {
      console.error(`Error en respuesta de ${url}:`, {
        status: error.response?.status,
        data: error.response?.data,
        error: error.message
      });
    }
    
    // Si es error 429, esperar y reintentar automáticamente, pero con límite
    if (error.response?.status === 429 && error.config && !error.config.__retryCount) {
      error.config.__retryCount = 1; // Inicializar contador de reintentos
      
      console.warn("Recibido error 429, esperando antes de reintentar (intento 1/3)...");
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      return apiClient(error.config);
    } else if (error.response?.status === 429 && error.config && error.config.__retryCount < 3) {
      error.config.__retryCount++; // Incrementar contador
      
      console.warn(`Recibido error 429, esperando antes de reintentar (intento ${error.config.__retryCount}/3)...`);
      await new Promise(resolve => setTimeout(resolve, error.config.__retryCount * 3000)); // Backoff exponencial
      
      return apiClient(error.config);
    }
    
    return Promise.reject(error);
  }
);

// Estados de perfil para el paciente
export const PROFILE_STATUS = {
  NONE: 'none',                       // No tiene perfil
  PENDING: 'pending_verification',    // Perfil creado pero pendiente de verificación
  ACTIVE: 'active',                   // Perfil activo y verificado
  INACTIVE: 'inactive'                // Perfil inactivo o suspendido
};

// Configuración para uso en componentes
export const CONFIG = {
  API_URL,
  PROFILE_STATUS,
  COUNTRIES: {
    cl: {
      name: 'Chile',
      code: 'cl',
      idType: 'RUT',
      idFormat: '12345678-9'
    },
    us: {
      name: 'Estados Unidos',
      code: 'us',
      idType: 'SSN',
      idFormat: '123-45-6789'
    }
  }
};

// Mock health centers data para usar como fallback
const mockHealthCenters = {
  us: [
    { id: 1, name: 'Mayo Clinic' },
    { id: 2, name: 'Cleveland Clinic' },
    { id: 3, name: 'Johns Hopkins Hospital' },
    { id: 4, name: 'Massachusetts General Hospital' },
    { id: 5, name: 'UCLA Medical Center' },
    { id: 6, name: 'New York-Presbyterian Hospital' },
    { id: 7, name: 'Stanford Health Care' },
    { id: 8, name: 'UCSF Medical Center' },
  ],
  cl: [
    { id: 1, name: 'Clínica Las Condes' },
    { id: 2, name: 'Hospital Clínico Universidad de Chile' },
    { id: 3, name: 'Clínica Alemana' },
    { id: 4, name: 'Hospital del Salvador' },
    { id: 5, name: 'Hospital Barros Luco Trudeau' },
    { id: 6, name: 'Clínica Santa María' },
    { id: 7, name: 'Hospital Sótero del Río' },
    { id: 8, name: 'Hospital San José' },
    { id: 9, name: 'Centro de Salud Familiar Rauco' },
  ]
};

/**
 * Función para normalizar los valores de signos vitales desde el formato DynamoDB
 * @param {Object} vitalSigns - Objeto de signos vitales con posible formato DynamoDB
 * @returns {Object} - Objeto normalizado con valores simples
 */
const normalizeVitalSigns = (vitalSigns) => {
  if (!vitalSigns) return {};
  
  const normalized = {};
  
  Object.keys(vitalSigns).forEach(key => {
    const vitalSign = vitalSigns[key];
    // Verificar si tiene formato DynamoDB
    if (vitalSign && typeof vitalSign === 'object') {
      if (vitalSign.N !== undefined) {
        normalized[key] = parseFloat(vitalSign.N);
      } else if (vitalSign.S !== undefined) {
        // Intentar convertir a número si es posible
        const numValue = parseFloat(vitalSign.S);
        normalized[key] = isNaN(numValue) ? vitalSign.S : numValue;
      } else {
        normalized[key] = vitalSign;
      }
    } else {
      normalized[key] = vitalSign;
    }
  });
  
  return normalized;
};

/**
 * Servicio para comunicación con el backend de AGT Health (Pacientes)
 */
const agthealthPatientService = {
  /**
   * Verifica si un ID ya está registrado
   * @param {Object} data Datos para la verificación
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  checkIdentificationExists: async (data) => {
    try {
      const requestKey = `checkId_${data.identificationNumber}_${data.countryCode}`;
      
      return getOrCreateRequest(requestKey, async () => {
        const response = await retryWithExponentialBackoff(() => 
          apiClient.post('/auth/check-id', data)
        );
        return response.data;
      });
    } catch (error) {
      console.error('Error al verificar identificación:', error);
      return { success: false, exists: false, error: error.message };
    }
  },

  /**
   * Obtiene los centros de salud por país
   * @param {string} countryCode Código del país (us, cl)
   * @returns {Promise<Object>} Respuesta con los centros de salud
   */
  getHealthCentersByCountry: async (countryCode) => {
    const requestKey = `healthCenters_${countryCode}`;
    
    return getOrCreateRequest(requestKey, async () => {
      try {
        // Verificar si tenemos datos en caché y si son recientes (menos de 5 minutos)
        const now = Date.now();
        const cachedData = apiCache.healthCenters[countryCode];
        const lastFetch = apiCache.lastFetch[countryCode] || 0;
        
        // Si hay datos en caché y son recientes, usarlos
        if (cachedData && (now - lastFetch < 300000)) { // 5 minutos
          return cachedData;
        }
        
        // Si no hay caché o está obsoleta, hacer la petición
        const response = await retryWithExponentialBackoff(() => 
          apiClient.get(`/health-centers/${countryCode}`)
        );
        
        // Guardar en caché
        apiCache.healthCenters[countryCode] = response.data;
        apiCache.lastFetch[countryCode] = now;
        
        return response.data;
      } catch (error) {
        console.error('Error al obtener centros de salud:', error);
        
        // Si hay error 429, intentar usar caché aunque esté obsoleta
        if (error.response?.status === 429) {
          console.warn('Demasiadas solicitudes (429), usando caché disponible');
          const cachedData = apiCache.healthCenters[countryCode];
          if (cachedData) return cachedData;
        }
        
        // Si no hay caché o el error es diferente, devolver datos mock o vacío
        return { success: false, data: mockHealthCenters[countryCode] || [], error: error.message };
      }
    });
  },

  /**
   * Busca centros de salud por nombre
   * @param {string} countryCode Código del país
   * @param {string} query Término de búsqueda
   * @returns {Promise<Object>} Respuesta con los centros encontrados
   */
  searchHealthCenters: async (countryCode, query) => {
    const cacheKey = `search_${countryCode}_${query}`;
    const requestKey = cacheKey;
    
    return getOrCreateRequest(requestKey, async () => {
      try {
        // Verificar caché de búsqueda
        if (apiCache.searchResults[cacheKey]) {
          return apiCache.searchResults[cacheKey];
        }
        
        const response = await retryWithExponentialBackoff(() => 
          apiClient.get(`/health-centers/search?countryCode=${countryCode}&query=${encodeURIComponent(query)}`)
        );
        
        // Guardar en caché
        apiCache.searchResults[cacheKey] = response.data;
        
        // Limpiar caché de búsquedas después de un tiempo
        setTimeout(() => {
          delete apiCache.searchResults[cacheKey];
        }, 300000); // 5 minutos
        
        return response.data;
      } catch (error) {
        console.error('Error al buscar centros de salud:', error);
        
        // Intentar filtrar localmente como fallback
        const centers = apiCache.healthCenters[countryCode] || mockHealthCenters[countryCode] || [];
        const filtered = centers.filter(center => 
          center.name.toLowerCase().includes(query.toLowerCase())
        );
        
        return { success: true, data: filtered, error: error.message, isLocalFilter: true };
      }
    });
  },

  /**
   * Crea un perfil de paciente
   * @param {Object} profileData Datos del perfil
   * @returns {Promise<Object>} Respuesta con el perfil creado
   */
  createProfile: async (profileData) => {
    try {
      const response = await retryWithExponentialBackoff(() => 
        apiClient.post('/patient-profiles', profileData)
      );
      
      // Guardar datos del perfil en localStorage
      if (response.data.success && response.data.data.profile) {
        localStorage.setItem('agthealth_patient_profile', JSON.stringify(response.data.data.profile));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al crear perfil de paciente:', error);
      return { success: false, data: null, error: error.message };
    }
  },

  /**
   * Obtiene el perfil del paciente
   * @returns {Promise<Object>} Respuesta con el perfil
   */
  getProfile: async () => {
    try {
      
      const response = await retryWithExponentialBackoff(() => 
        apiClient.get('/patient-profiles')
      );
      
      // Actualizar cache del perfil
      if (response.data.success && response.data.data) {
        localStorage.setItem('agthealth_patient_profile', JSON.stringify(response.data.data));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener perfil de paciente:', error);
      
      // Intentar usar caché local si hay error
      const cachedProfile = localStorage.getItem('agthealth_patient_profile');
      if (cachedProfile) {
        try {
          const parsed = JSON.parse(cachedProfile);
          return { success: true, data: parsed, fromCache: true };
        } catch (e) {
          console.error('Error al parsear perfil en caché:', e);
        }
      }
      
      // En lugar de propagar el error, devolver un objeto de respuesta con éxito falso
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },

  /**
   * Verifica si existe un registro médico para una identificación
   * @param {string} identificationNumber - Número de identificación (RUT/SSN)
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  checkMedicalRecord: async (identificationNumber) => {
    const requestKey = `checkMedicalRecord_${identificationNumber}`;
    
    return getOrCreateRequest(requestKey, async () => {
      try {
        const response = await retryWithExponentialBackoff(() => 
          apiClient.post('/users/check-medical-record', { identificationNumber })
        );
        return response.data;
      } catch (error) {
        console.error('Error al verificar registro médico:', error);
        return { success: false, exists: false, error: error.message };
      }
    });
  },

  /**
   * Verifica si un centro de salud tiene registro del paciente
   * @param {Object} data - Datos para la verificación
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  checkCenterRecord: async (data) => {
    const requestKey = `checkCenterRecord_${data.identificationNumber}_${data.healthCenterId}`;
    
    return getOrCreateRequest(requestKey, async () => {
      try {
        const response = await retryWithExponentialBackoff(() => 
          apiClient.post('/users/check-center-record', data)
        );
        return response.data;
      } catch (error) {
        console.error('Error al verificar registro en centro:', error);
        return { success: false, hasRecord: false, error: error.message };
      }
    });
  },

  /**
   * Solicita vincular un centro de salud
   * @param {Object} data - Datos de la solicitud
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  requestCenterLink: async (data) => {
    try {
      // Adaptar datos al formato que espera el backend
      const requestData = {
        centerName: data.centerName || "",
        healthCenterId: data.healthCenterId || null,
        customMessage: data.customMessage || "",
        // Incluir explícitamente estos campos
        identificationNumber: data.identificationNumber || null,
        countryCode: data.countryCode || null
      };
  
      const response = await retryWithExponentialBackoff(() => 
        apiClient.post('/users/request-center-link', requestData)
      );
  
      return response.data;
    } catch (error) {
      console.error('Error al solicitar vinculación de centro:', error);
      if (error.response) {
        console.error('Datos de respuesta de error:', error.response.data);
      }
      return { 
        success: false, 
        error: error.message 
      };
    }
  },

  /**
   * Obtiene los registros médicos del paciente
   * @param {Object} options - Opciones de filtrado (fechas)
   * @returns {Promise<Object>} Respuesta con los registros médicos
   */
  getPatientRecords: async (options = {}) => {
    try {
      let url = '/patient-records';
      const queryParams = [];
  
      if (options.startDate) {
        queryParams.push(`startDate=${encodeURIComponent(options.startDate)}`);
      }
  
      if (options.endDate) {
        queryParams.push(`endDate=${encodeURIComponent(options.endDate)}`);
      }
  
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }

      const response = await retryWithExponentialBackoff(() =>
        apiClient.get(url, {
          validateStatus: (status) => {
            // Aceptar 404 como respuesta válida (sin generar error en consola)
            return (status >= 200 && status < 300) || status === 404;
          }
        })
      );

      // Si es 404, devolver respuesta vacía exitosa
      if (response.status === 404) {
        return {
          success: true,
          data: [],
          count: 0
        };
      }

      // Verificar la estructura de la respuesta
      if (response.data && response.data.success) {
        // Asegurarse de que los datos sean un array
        const recordsData = Array.isArray(response.data.data) 
          ? response.data.data 
          : (response.data.data ? [response.data.data] : []);
        
        // Verificar que cada registro tenga vitalSigns correctamente formateado
        const processedRecords = recordsData.map(record => {
          // Si vitalSigns está presente pero no está correctamente formateado
          if (record.vitalSigns && typeof record.vitalSigns === 'object') {
            // Intentar normalizar vitalSigns si tiene formato DynamoDB
            record.vitalSigns = normalizeVitalSigns(record.vitalSigns);
          }
          return record;
        });
        
        return {
          success: true,
          data: processedRecords,
          count: processedRecords.length
        };
      }
      
      return { 
        success: false, 
        data: [], 
        error: response.data?.message || 'Error desconocido' 
      };
    } catch (error) {
      // Silenciar el log si es un 404 (no hay registros todavía es válido)
      if (error.response?.status !== 404) {
        console.error('Error al obtener registros médicos:', error);
      }
      return {
        success: false,
        data: [],
        error: error.response?.status === 404 ? 'No hay registros médicos disponibles' : error.message
      };
    }
  },
  
  /**
   * Obtiene tendencias de una métrica específica de registros médicos
   * @param {string} metric - Métrica a consultar (ej: 'heartRate')
   * @param {string} period - Período de tiempo ('week', 'month', 'year')
   * @returns {Promise<Object>} Respuesta con los datos de tendencia
   */
  getPatientRecordTrends: async (metric, period = 'month') => {
    const requestKey = `trends_${metric}_${period}`;
  
    return getOrCreateRequest(requestKey, async () => {
      try {
        const response = await retryWithExponentialBackoff(() => 
          apiClient.get(`/patient-records/trends?metric=${encodeURIComponent(metric)}&period=${encodeURIComponent(period)}`)
        );
        
        // Verificar y procesar los datos si es necesario
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          const processedData = response.data.data.map(item => {
            // Asegurarse de que el valor sea un número cuando sea posible
            if (item.value !== undefined && item.value !== null) {
              const numValue = parseFloat(item.value);
              if (!isNaN(numValue)) {
                item.value = numValue;
              }
            }
            return item;
          });
          
          return {
            success: true,
            data: processedData,
            metric,
            period
          };
        }
  
        return response.data;
      } catch (error) {
        console.error('Error al obtener tendencias de registros médicos:', error);
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    });
  },

  /**
   * Obtiene las citas médicas del paciente
   * @param {Object} options - Opciones de filtrado (status)
   * @returns {Promise<Object>} Respuesta con las citas médicas
   */
  getPatientAppointments: async (options = {}) => {
    try {
      let url = '/appointments';
      const queryParams = [];
  
      if (options.status) {
        queryParams.push(`status=${encodeURIComponent(options.status)}`);
      }
  
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }

      const response = await retryWithExponentialBackoff(() =>
        apiClient.get(url, {
          validateStatus: (status) => {
            // Aceptar 404 como respuesta válida (sin generar error en consola)
            return (status >= 200 && status < 300) || status === 404;
          }
        })
      );

      // Si es 404, devolver respuesta vacía exitosa
      if (response.status === 404) {
        return {
          success: true,
          data: { upcoming: [], past: [] }
        };
      }

      if (response.data && response.data.success) {
        return response.data;
      }
  
      return { 
        success: false, 
        data: { upcoming: [], past: [] }, 
        error: response.data?.message || 'Error desconocido' 
      };
    } catch (error) {
      // Silenciar el log si es un 404 (no hay citas todavía es válido)
      if (error.response?.status !== 404) {
        console.error('Error al obtener citas médicas:', error);
      }
      return {
        success: false,
        data: { upcoming: [], past: [] },
        error: error.response?.status === 404 ? 'No hay citas médicas disponibles' : error.message
      };
    }
  },

  /**
   * Obtiene los profesionales independientes por país
   * @param {string} countryCode Código del país (us, cl)
   * @returns {Promise<Object>} Respuesta con los profesionales independientes
   */
  getProfessionalsByCountry: async (countryCode) => {
    const requestKey = `professionals_${countryCode}`;
  
    return getOrCreateRequest(requestKey, async () => {
      try {
        // Verificar si tenemos datos en caché y si son recientes (menos de 5 minutos)
        const now = Date.now();
        const cachedData = apiCache.professionals && apiCache.professionals[countryCode];
        const lastFetch = (apiCache.lastFetch && apiCache.lastFetch[`professionals_${countryCode}`]) || 0;
  
        // Si hay datos en caché y son recientes, usarlos
        if (cachedData && (now - lastFetch < 300000)) { // 5 minutos
          return cachedData;
        }
  
        // Si no hay caché o está obsoleta, hacer la petición
        const response = await retryWithExponentialBackoff(() => 
          apiClient.get(`/independent-professionals/${countryCode}`)
        );
  
        // Guardar en caché
        if (!apiCache.professionals) apiCache.professionals = {};
        if (!apiCache.lastFetch) apiCache.lastFetch = {};
  
        apiCache.professionals[countryCode] = response.data;
        apiCache.lastFetch[`professionals_${countryCode}`] = now;
  
        return response.data;
      } catch (error) {
        console.error('Error al obtener profesionales independientes:', error);
        return { success: false, data: [], error: error.message };
      }
    });
  },
  
  /**
   * Busca profesionales independientes por nombre
   * @param {string} countryCode Código del país
   * @param {string} query Término de búsqueda
   * @returns {Promise<Object>} Respuesta con los profesionales encontrados
   */
  searchProfessionals: async (countryCode, query) => {
    const cacheKey = `search_professionals_${countryCode}_${query}`;
    const requestKey = cacheKey;
  
    return getOrCreateRequest(requestKey, async () => {
      try {
        // Verificar caché de búsqueda
        if (apiCache.searchResults && apiCache.searchResults[cacheKey]) {
          return apiCache.searchResults[cacheKey];
        }
  
        const response = await retryWithExponentialBackoff(() => 
          apiClient.get(`/independent-professionals/search?countryCode=${countryCode}&query=${encodeURIComponent(query)}`)
        );
  
        // Guardar en caché
        if (!apiCache.searchResults) apiCache.searchResults = {};
        apiCache.searchResults[cacheKey] = response.data;
  
        // Limpiar caché de búsquedas después de un tiempo
        setTimeout(() => {
          if (apiCache.searchResults) {
            delete apiCache.searchResults[cacheKey];
          }
        }, 300000); // 5 minutos
  
        return response.data;
      } catch (error) {
        console.error('Error al buscar profesionales independientes:', error);
        return { success: false, data: [], error: error.message };
      }
    });
  },

  /**
   * Verifica si un usuario tiene profesionales independientes asociados
   * @param {string} identificationNumber - Número de identificación (RUT/SSN)
   * @returns {Promise<Object>} Respuesta con los profesionales asociados
   */
  checkUserProfessionals: async (identificationNumber) => {
    const requestKey = `checkUserProfessionals_${identificationNumber}`;
  
    return getOrCreateRequest(requestKey, async () => {
      try {
        const response = await retryWithExponentialBackoff(() => 
          apiClient.post('/users/check-user-professionals', { identificationNumber })
        );
        return response.data;
      } catch (error) {
        console.error('Error al verificar profesionales asociados:', error);
        return { 
          success: false, 
          hasProfessionals: false, 
          professionals: [],
          error: error.message 
        };
      }
    });
  },
};

export default agthealthPatientService;
